import { api } from '../lib/api'
import { apiErrorBanner, parseApiError } from '../lib/api-error'
import i18n from '../i18n'
import type { components, operations } from '../api-client/types'

// Base API service class
export class ApiService {
  protected api = api

  // Helper method to handle API responses
  protected async handleResponse<T>(response: Promise<{ data: T }>): Promise<T> {
    try {
      const result = await response
      const payload = result.data as any
      return payload?.data !== undefined ? payload.data : payload
    } catch (error: any) {
      const parsed = parseApiError(error)
      // Prefer the API's own message. Known error codes are only a fallback when
      // the body has no useful text — a recognized code must not hide `message`.
      const msg = apiErrorBanner(parsed, {
        network: i18n.t('errors.network'),
        unknown: i18n.t('errors.unknownServer'),
      })
      if (error && typeof error === 'object') {
        error.userMessage = msg
      }
      throw error
    }
  }
}

// Type definitions for better type safety
export type PaginatedResponse<T> = {
  data: T[]
  total: number
  page: number
  limit: number
}

// Auth related types are now defined in auth.service.ts
// (AdminLoginResponse, AuthTokenResponse, MfaSetupResponse, etc.)

// User related types
export type User = components['schemas']['UserResponseDto']
export type CreateUserRequest = components['schemas']['CreateUserDto']
export type UpdateUserRequest = components['schemas']['UpdateUserDto']

// Company related types
export type Company = components['schemas']['CompanyResponseDto']
export type CreateCompanyRequest = components['schemas']['CreateCompanyDto']
export type UpdateCompanyRequest = components['schemas']['UpdateCompanyDto']

// Module related types
export type Module = components['schemas']['ModuleResponseDto']
export type CreateModuleRequest =
  | components['schemas']['CreateModuleDto']
  | components['schemas']['CreateModuleWithChaptersDto']
export type UpdateModuleRequest = components['schemas']['UpdateModuleDto']
export type AssignModuleRequest = components['schemas']['AssignModuleDto']

// Progress related types
export type Progress = components['schemas']['ProgressResponseDto']
export type QuizSubmission = components['schemas']['QuizSubmissionDto']
export type QuizResult = components['schemas']['QuizResultDto']

// Query parameters types - using generated types from API client
export type CompaniesQuery = operations['CompaniesController_findAll']['parameters']['query']
export type ModulesQuery = operations['ModulesController_findAll']['parameters']['query']
export type UsersQuery = operations['UsersController_findAll']['parameters']['query']

// Legacy aliases for backward compatibility
export type PaginationQuery = CompaniesQuery
export type ModuleQuery = ModulesQuery
export type UserQuery = UsersQuery

// Specific paginated response types for each service
export type ModulesPaginatedResponse = {
  modules: Module[]
  total: number
  page: number
  limit: number
}

export type UsersPaginatedResponse = {
  users: User[]
  total: number
  page: number
  limit: number
}

export type CompaniesPaginatedResponse = {
  companies: Company[]
  total: number
  page: number
  limit: number
}
